<script setup>
// FLOW-03a (SPEC S2, S4, S7; PLAN §2.3; Task 7.3) — a página do detalhe de um gatilho, na rota
// `powerflow/flows/:nome/gatilhos/:gatilhoId`, dentro da seção Flows: acha o gatilho pelo nome do
// flow e pelo `id` dele no YAML (`GET /powerflow/gatilhos?flow_name=`), e o entrega ao detalhe
// aprovado no mockup (`GatilhoDoFlowDetalhe`), que só mostra.
//
// - **O webhook**: as últimas 50 chamadas (`GET .../chamadas`), "Gerar URL nova" (`trocar_token`,
//   com a URL nova na resposta) e o exemplo de `curl`, com o corpo montado dos `params` da publicada.
// - **A agenda**: as próximas três batidas (a `proxima_em` do PowerTools e, dali, o intervalo; no cron,
//   a prévia) e a última batida, que é o histórico que o PowerTools guarda (P17).
// - **Ligar e pausar** mudam na hora e voltam atrás na recusa, com a frase do servidor (o 422
//   `gatilho_so_do_rascunho`); depois, o gatilho é relido, para a próxima batida.
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import powerflowFlowsApi from 'dashboard/api/powerflowFlows';
import powerflowGatilhosApi from 'dashboard/api/powerflowGatilhos';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import GatilhoDoFlowDetalhe from './GatilhoDoFlowDetalhe.vue';
import PainelDeFalha from './PainelDeFalha.vue';

const props = defineProps({
  nome: { type: String, required: true },
  gatilhoId: { type: String, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => Number(route.params.accountId));

const gatilho = ref(null);
const publicada = ref(null);
const chamadas = ref([]);
const proximas = ref([]);
const falha = ref('');

const EXEMPLOS = { integer: 1, number: 1.5, boolean: true, array: [] };
const corpoDeExemplo = computed(() => {
  const campos = publicada.value?.args_schema?.properties || {};
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(campos).map(([nome, def]) => [
        nome,
        def?.type in EXEMPLOS ? EXEMPLOS[def.type] : '…',
      ])
    )
  );
});

// A última batida da agenda, na forma de uma chamada.
const batidas = agenda => {
  const estado = agenda.estado_da_agenda;
  if (!estado?.ultima_prevista || !estado.ultimo_desfecho) return [];
  return [
    {
      at: estado.ultima_prevista,
      desfecho: estado.ultimo_desfecho,
      execucao_id: estado.ultima_execucao_id,
    },
  ];
};

async function proximasDaAgenda(agenda) {
  const primeira = agenda.estado_da_agenda?.proxima_em;
  if (!agenda.ligado || !primeira) return [];
  const passo = Number(agenda.config?.a_cada_s) * 1000;
  if (passo) {
    const em = Date.parse(primeira);
    return [0, 1, 2].map(k => new Date(em + k * passo).toISOString());
  }
  try {
    return (await powerflowGatilhosApi.previa(accountId.value, agenda.config))
      .proximas;
  } catch {
    return [primeira];
  }
}

async function carregar() {
  falha.value = '';
  try {
    const [lista, { flows }] = await Promise.all([
      powerflowGatilhosApi.index(accountId.value, { flowName: props.nome }),
      powerflowFlowsApi.index(accountId.value),
    ]);
    const achado = lista.find(g => g.gatilho_id === props.gatilhoId);
    if (!achado) {
      falha.value = t('POWER_TOOLS.POWERFLOW.TRIGGERS.NOT_FOUND');
      return;
    }
    publicada.value = flows.find(f => f.nome === props.nome)?.publicada || null;
    if (achado.tipo === 'webhook') {
      chamadas.value = await powerflowGatilhosApi.chamadas(
        accountId.value,
        achado.id
      );
    } else {
      chamadas.value = batidas(achado);
      proximas.value = await proximasDaAgenda(achado);
    }
    gatilho.value = achado;
  } catch {
    falha.value = t('POWER_TOOLS.POWERFLOW.TRIGGERS.LOAD_ERROR');
  }
}

const mensagemDoServidor = erro => {
  const recusa = erro?.response?.data?.erro;
  return recusa
    ? [recusa.mensagem, recusa.como_resolver].filter(Boolean).join(' ')
    : null;
};

async function alternar(ligado) {
  gatilho.value.ligado = ligado;
  try {
    await powerflowGatilhosApi.alternar(
      accountId.value,
      gatilho.value.id,
      ligado
    );
    await carregar();
  } catch (erro) {
    gatilho.value.ligado = !ligado;
    useAlert(
      mensagemDoServidor(erro) ||
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.TOGGLE_ERROR')
    );
  }
}

async function trocarToken() {
  try {
    const novo = await powerflowGatilhosApi.trocarToken(
      accountId.value,
      gatilho.value.id
    );
    gatilho.value = {
      ...gatilho.value,
      url: novo.url,
      url_de_teste: novo.url_de_teste,
    };
    useAlert(t('POWER_TOOLS.POWERFLOW.TRIGGERS.ROTATE_DONE'));
  } catch (erro) {
    useAlert(
      mensagemDoServidor(erro) ||
        t('POWER_TOOLS.POWERFLOW.TRIGGERS.ROTATE_ERROR')
    );
  }
}

const copiar = texto => copyTextToClipboard(texto);

const voltar = () =>
  router.push({
    name: 'powerflow_flows',
    params: { accountId: accountId.value },
  });

const abrirExecucao = execucaoId =>
  router.push({
    name: 'powerflow_execucao',
    params: { accountId: accountId.value, execucaoId },
  });

onMounted(carregar);
</script>

<template>
  <div class="flex-1 min-w-0 px-6 pt-4 pb-8 overflow-y-auto">
    <div class="flex flex-col max-w-6xl gap-4">
      <PainelDeFalha v-if="falha" :mensagem="falha" @tentar="carregar" />
      <GatilhoDoFlowDetalhe
        v-else-if="gatilho"
        :gatilho="gatilho"
        :versao="publicada?.versao || null"
        :chamadas="chamadas"
        :proximas="proximas"
        :corpo-de-exemplo="corpoDeExemplo"
        @alternar="alternar"
        @trocar-token="trocarToken"
        @copiar="copiar"
        @abrir-execucao="abrirExecucao"
        @voltar="voltar"
      />
      <div
        v-else
        role="status"
        class="flex items-center justify-center gap-2 px-6 py-12 text-sm border rounded-pz border-n-weak bg-n-card shadow-pz text-n-slate-11"
        data-gatilho-carregando
      >
        <Spinner />
      </div>
    </div>
  </div>
</template>
